/* eslint-disable @typescript-eslint/consistent-type-assertions */

import Logo from "components/Logo";
import ActionTemplateEditor from "components/ActionTemplateEditor/ActionTemplateEditor";
import * as React from "react";
import { DeploymentActionResource, HasManualInterventionResponsibleTeams, TenantedDeploymentMode, ActionTemplateResource, RunConditionForAction, ProcessType } from "client/resources";
import { AdditionalActions } from "components/Actions/pluginRegistry";
import { difference, intersection, isEqual, uniq, keyBy } from "lodash";
import { repository } from "clientInstance";
import { required, Note, UnstructuredFormSection, ExpansionButtons } from "components/form";
import FeatureEditor from "components/FeatureEditor/FeatureEditor";
import { ExpandableFormSection, FormSectionHeading, Summary } from "components/form";
import { Callout, CalloutType } from "primitiveComponents/dataDisplay/Callout";
import { ChannelMultiSelect } from "components/MultiSelect";
import { Feature } from "components/FeatureToggle";
import FeatureToggle from "components/FeatureToggle/FeatureToggle";
import Checkbox from "primitiveComponents/form/Checkbox/Checkbox";
import ContextualMissingChip, { ChannelChip, ChipIcon } from "components/Chips";
import ParseHelper from "utils/ParseHelper/ParseHelper";
import StartTriggerExpander from "../Process/Common/StartTriggerExpander";
import RunTriggerExpander from "../Process/Common/RunTriggerExpander";
import PackageRequirementExpander from "../Process/Common/PackageRequirementExpander";
import OpenFeatureDialog from "components/OpenFeatureDialog/OpenFeatureDialog";
import { useSelector } from "react-redux";
import Environments from "../Process/Common/Environments";
import ExecutionPlan from "../Process/Common/ExecutionPlan";
import TenantsExpander from "../Process/Common/TenantsExpander";
import ActionProperties from "client/resources/actionProperties";
import getActionLogoUrl from "../getActionLogoUrl";
import { PackageRequirement } from "client/resources";
import { PackageReference } from "client/resources/packageReference";
import StepName from "../Process/Common/StepName";
import { OverflowMenuItems, OverflowMenu } from "components/Menu";
import { WithProjectContextInjectedProps, useProjectContext } from "../../context";
import { MenuItem } from "components/Menu/OverflowMenu";
import FeedResource from "client/resources/feedResource";
import RunTriggerForChildActionExpander from "../Process/Common/RunTriggerForChildActionExpander";
import { WithOptionalRunbookContextInjectedProps, useOptionalRunbookContext } from "../Runbooks/RunbookContext";
import { memoize } from "lodash";
import { ScriptActionContext } from "components/Actions/script/ScriptActionContext";
import { Errors } from "components/DataBaseComponent/Errors";
import { whereToRun, runsOnServer, processScopedEditPermission, deleteActionAndRedirect, isRunOnServerOrWorkerPool, isVersionControlledProcess, getDeleteProcessMenuItem } from "./Common/CommonProcessHelpers";
import { generateGuid } from "./generation";
import { ProcessStepActionData } from "./ProcessStepsLayout";
import PaperLayout from "components/PaperLayout";
import ActionList from "components/ActionList";
import { DoBusyTask } from "components/DataBaseComponent";
import BaseComponent from "components/BaseComponent";
import { WithProcessContextInjectedProps, useProcessContext } from "./Contexts/ProcessContext";
import { StoredStep, StoredAction, RunOnServerOrWorkerPool, EnvironmentOption, RunOn, RunOnDeploymentTarget, ExecutionLocation } from "./types";
import { ConflictConfirmationDialog } from "./Common/ConflictConfirmationDialog";
import { ErrorsForAction } from "./Common/ErrorsForAction";
import { WithProcessErrorSelectorContextInjectedProps, useProcessErrorSelectors } from "./Contexts/ProcessErrors/ProcessErrorsContext";
import { DebounceText } from "primitiveComponents/form/Text/Text";
import { WithProcessQueryStringContextInjectedProps, useProcessQueryStringContext } from "./Contexts/ProcessQueryString/ProcessQueryStringContext";
import { ExpandableContainer } from "components/Expandable";
import ExpandExpanders from "components/Expandable/ExpandExpanders";
import ExpandAllExpanders from "components/Expandable/ExpandAllExpanders";
import { WithProcessWarningSelectorContextInjectedProps, useProcessWarningSelectors } from "./Contexts/ProcessWarnings/ProcessWarningsContext";
import { KeyedItemProps } from "components/KeyAccessProvider/types";
import Lookup from "components/Lookup";
import Markdown from "components/Markdown";
import MarkdownEditor from "components/form/MarkdownEditor/MarkdownEditor";
import { useKeyedItemAccess } from "components/KeyAccessProvider/KeyedItemAccessProvider";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "./Contexts/ProcessFeedsContextProvider";
import ActionTemplateSearchResource from "client/resources/actionTemplateSearchResource";
import { useActionTemplatesFromContext } from "./Contexts/ProcessActionTemplatesContextProvider";
import { WarningsForAction } from "./Common/WarningsForAction";
import { ProjectActionPropertiesEditor } from "components/ActionPropertiesEditor/ProjectActionPropertiesEditor";
import ActionInputs from "client/resources/actionInputs";

interface GlobalConnectedProps {
    isConfigurationAsCodeForRunbooksEnabled?: boolean;
}

//TODO: @Cleanup - We want to remove the state from this component if possible. Functional components would make this easier to reason about.
interface ProcessActionDetailsState {
    runOnServerOrWorkerPoolCopy: RunOnServerOrWorkerPool | null;
    runOn: RunOn;
    environmentOption: EnvironmentOption;
}

export type ProcessActionDetailsProps = {
    processType: ProcessType;
    isNew: boolean;
    actionType?: string;
    parentStepId?: string;
    reloadKey?: string;
    errors: Errors | undefined;
    doBusyTask: DoBusyTask;
    busy?: Promise<void>;
    step: StoredStep;
    action: StoredAction;
    setCurrentActionName: (name: string) => void;
    setCurrentStepName: (name: string) => void;
    refreshStepLookups: () => void;
} & ProcessStepActionData;

interface GlobalConnectedProps {
    isBuiltInWorkerEnabled: boolean;
}

type Props = ProcessActionDetailsProps &
    WithProjectContextInjectedProps &
    WithProcessContextInjectedProps &
    WithOptionalRunbookContextInjectedProps &
    WithProcessQueryStringContextInjectedProps &
    WithProcessErrorSelectorContextInjectedProps &
    WithProcessWarningSelectorContextInjectedProps &
    KeyedItemProps &
    GlobalConnectedProps & {
        feeds: FeedResource[];
        actionTemplates: ActionTemplateSearchResource[];
        refreshFeeds: () => Promise<unknown>;
    };

const AutoExpandActionErrors: React.FC<{ actionId: string }> = (props) => {
    const selectors = useProcessErrorSelectors();
    const processContext = useProcessContext();

    const getErrors = React.useCallback(() => Object.keys(selectors.getActionFieldErrors(props.actionId, processContext.selectors)), [selectors, props.actionId, processContext.selectors]);

    return <ExpandExpanders getKeysToExpand={getErrors} />;
};

const AutoExpandActionWarnings: React.FC<{ actionId: string }> = (props) => {
    const selectors = useProcessWarningSelectors();
    const processContext = useProcessContext();
    const getWarnings = React.useCallback(() => Object.keys(selectors.getActionFieldWarnings(props.actionId, processContext.selectors)), [selectors, props.actionId, processContext.selectors]);
    return <ExpandExpanders getKeysToExpand={getWarnings} />;
};

export interface EnvironmentSelection {
    unavailable: string[];
    unavailableExclusive: string[];
    inclusive: string[];
    exclusive: string[];
}

class ProcessActionDetailsInternal extends BaseComponent<Props, ProcessActionDetailsState> {
    private get isNew(): boolean {
        return this.props.isNew;
    }

    private get environmentSelection(): EnvironmentSelection {
        const environmentsByKey = keyBy(this.props.stepLookups.environments, this.props.itemKey);
        const action = this.props.action;

        const knownEnvironments = Object.keys(environmentsByKey);
        const unavailableEnvironments = difference(action.Environments || [], knownEnvironments);
        const unavailableExcludedEnvironments = difference(action.ExcludedEnvironments || [], knownEnvironments);
        const inclusiveEnvironments = intersection(action.Environments || [], knownEnvironments);
        const exclusiveEnvironments = intersection(action.ExcludedEnvironments || [], knownEnvironments);

        return {
            unavailable: unavailableEnvironments,
            unavailableExclusive: unavailableExcludedEnvironments,
            inclusive: inclusiveEnvironments,
            exclusive: exclusiveEnvironments,
        };
    }

    private get hasHiddenEnvironments(): boolean {
        const selection = this.environmentSelection;
        return selection.unavailableExclusive.length > 0 || selection.unavailable.length > 0;
    }

    //Memoize does not take all arguments into account for the cache key.
    private memoizeLoadVariables = memoize(
        (projectId: string, runbookId?: string) => {
            return runbookId ? repository.Variables.getNamesForRunbookProcess(this.props.projectContext.state.model.Id, runbookId) : repository.Variables.getNamesForDeploymentProcess(projectId);
        },
        (...args) => args.join("_")
    );

    constructor(props: Props) {
        super(props);
        this.state = this.getStateUpdate();
    }

    getStateUpdate(): ProcessActionDetailsState {
        const props = this.props;
        const runOn = props.stepOther.runOn ?? new RunOnDeploymentTarget();
        const action = props.action;

        return {
            runOnServerOrWorkerPoolCopy: isRunOnServerOrWorkerPool(runOn) ? runOn : null,
            runOn,
            environmentOption: (action.Environments || []).length > 0 ? EnvironmentOption.Include : (action.ExcludedEnvironments || []).length > 0 ? EnvironmentOption.Exclude : EnvironmentOption.All,
        };
    }

    componentDidUpdate(prevProps: Props) {
        if (!isEqual(prevProps.stepOther, this.props.stepOther) || prevProps.action.Id !== this.props.action.Id) {
            this.setState(this.getStateUpdate());
        }

        // We need to keep these properties in sync for routing purposes.
        if (!isEqual(prevProps.action.Name, this.props.action.Name)) {
            this.props.setCurrentActionName(this.props.action.Name);
        }
        if (!isEqual(prevProps.step.Name, this.props.step.Name)) {
            this.props.setCurrentStepName(this.props.step.Name);
        }
    }

    refreshRunOn() {
        const runOn = whereToRun(!!this.props.step.Properties["Octopus.Action.TargetRoles"], this.props.action, this.props.stepLookups.availableWorkerPools, this.props.stepOther.plugin, this.props.stepOther.isBuiltInWorkerEnabled);
        this.setState({ runOn }, () => {
            this.setActionProperties({ ["Octopus.Action.RunOnServer"]: runOn.executionLocation === ExecutionLocation.DeploymentTarget ? "false" : "true" });
        });
    }

    isChildAction = () => {
        return this.props.processContext.selectors.isChildAction(this.props.action.Id);
    };

    doBusyForChildren = (action: () => Promise<void>): Promise<boolean> => {
        // don't clear errors on child tasks since they should just
        // be loading and we don't want to clear a Save error
        // just because we load some lookup data
        return this.props.doBusyTask(action, false);
    };

    loadVariables = () => {
        const runbookId = this.props.runbookContext?.state.runbook?.Id;
        const projectId = this.props.projectContext.state.model.Id;
        return this.memoizeLoadVariables(projectId, runbookId);
    };

    render() {
        const { step, action, processType, stepLookups, stepOther, projectContext, processContext, processQueryStringContext, busy, errors } = this.props;
        const { actions: contextActions, selectors: contextSelectors } = processContext;
        const { actions: queryStringActions } = processQueryStringContext;

        const addFeaturesElement =
            processContext.selectors.actionHasFeatures(action.Id) && !stepLookups.actionTemplate ? (
                <OpenFeatureDialog actionType={action.ActionType} properties={action.Properties} saveDone={(x) => this.setActionProperties({ ["Octopus.Action.EnabledFeatures"]: x })} />
            ) : undefined;

        const actionEditorAdditionalActions: AdditionalActions = {
            packageAcquisition: {
                stepPackageRequirement: step.PackageRequirement,
                onStepPackageRequirementChanged: (x: PackageRequirement) => {
                    this.setStepMetaProperties({ PackageRequirement: x });
                },
            },
            stepTargetRoles: step.Properties["Octopus.Action.TargetRoles"] as string,
            actionType: this.props.stepOther.plugin.actionType,
        };

        const hasManualInterventionResponsibleTeams = action && HasManualInterventionResponsibleTeams(action);

        const processEditPermission = { permission: processScopedEditPermission(processType), project: projectContext.state.model.Id, wildcard: true };
        const menuActions: Array<MenuItem | MenuItem[]> = [];
        if (step && step.Id) {
            // doesn't make sense to allow enable/disable/delete if the step hasn't been saved
            // it will also cause havoc - eg delete will delete *another* step.
            if (action) {
                menuActions.push(OverflowMenuItems.item(action.IsDisabled ? "Enable" : "Disable", () => this.handleEnabledToggle(), processEditPermission));
            }
            if (action) {
                menuActions.push(getDeleteProcessMenuItem("step", () => deleteActionAndRedirect(step, action, true, contextActions, contextSelectors, queryStringActions), processEditPermission, projectContext.state.model, step, action));
            } else {
                menuActions.push(getDeleteProcessMenuItem("parent step", () => deleteActionAndRedirect(step, action, true, contextActions, contextSelectors, queryStringActions), processEditPermission, projectContext.state.model, step, action));
            }

            if (stepLookups.actionTemplate) {
                menuActions.push(OverflowMenuItems.item("Detach Step Template", async () => this.handleDetachStepTemplate()));
            } else {
                menuActions.push(OverflowMenuItems.item("Extract Step Template", async () => this.handleCreateStepTemplate()));
            }
        }

        const actions = [];
        actions.push(addFeaturesElement);
        actions.push(<OverflowMenu menuItems={menuActions} />);

        const isChildAction = contextSelectors.isChildAction(action.Id);
        const stepActionNumber = isChildAction ? `${contextSelectors.getStepNumber(step.Id)}.${contextSelectors.getActionNumber(action.Id)}` : `${contextSelectors.getStepNumber(step.Id)}`;
        const actionErrors = action ? this.props.processErrorSelectors.getActionErrors(action.Id, processContext.selectors) : [];
        const actionWarnings = action ? this.props.processWarningSelectors.getActionWarnings(action.Id, processContext.selectors) : [];

        return (
            <ExpandableContainer containerKey={action.Id}>
                <PaperLayout
                    title={<StepName name={action.Name} number={stepActionNumber} stepType={stepOther.actionTypeName} />}
                    titleLogo={action && <Logo url={getActionLogoUrl(action)} />}
                    busy={undefined} // Handled by parent.
                    errors={undefined} // Handled by parent.
                    sectionControl={<ActionList actions={actions} />}
                    disableHeaderAnimations={true} // Disabling due to the way the ProcessStepDetailsLoader and this component work together.
                    fullWidth={true}
                    flatStyle={true}
                    hideHelpIcon={true}
                    disableScrollToActiveError={true} // We have custom error handling for our process form.
                    disableStickyHeader={true} // We have a custom layout above this which is already sticky.
                >
                    <ExpansionButtons containerKey={action.Id} errors={this.props.errors} expandAllOnMount={this.isNew} />
                    <AutoExpandActionErrors actionId={action.Id} />
                    <AutoExpandActionWarnings actionId={action.Id} />
                    <ErrorsForAction actionErrors={actionErrors} />
                    <WarningsForAction actionWarnings={actionWarnings} />
                    {(actionErrors.length === 0 || actionWarnings.length === 0) && this.props.isNew && <ExpandAllExpanders />}
                    <ScriptActionContext.Provider value={{ loadVariables: this.loadVariables }}>
                        <div>
                            <ConflictConfirmationDialog />

                            {action.IsDisabled && (
                                <UnstructuredFormSection stretchContent={true}>
                                    <Callout type={CalloutType.Warning} title={"This step is currently disabled"} />
                                </UnstructuredFormSection>
                            )}

                            <ExpandableFormSection
                                isExpandedByDefault={this.props.isNew}
                                errorKey="Name"
                                title="Step Name"
                                focusOnExpandAll
                                summary={action.Name ? Summary.summary(action.Name) : Summary.placeholder("Please enter a name for your step")}
                                help="A short, memorable, unique name for this step."
                            >
                                <DebounceText value={action.Name} onChange={(x) => this.setActionMetaProperties({ Name: x })} label="Step name" error={this.getFieldError("Name")} validate={required("Please enter a step name")} autoFocus={true} />
                            </ExpandableFormSection>

                            <ExpandableFormSection
                                isExpandedByDefault={this.props.isNew}
                                errorKey="Notes"
                                title="Notes"
                                summary={this.notesSummary()}
                                help="Enter markdown notes. The notes will be displayed in the step editor and on the process page."
                            >
                                <MarkdownEditor label="Notes" value={action.Notes || undefined} onChange={(notes) => this.setActionMetaProperties({ Notes: notes })} />
                            </ExpandableFormSection>

                            <ExpandableFormSection errorKey="IsDisabled" title="Enabled" summary={action.IsDisabled ? Summary.summary("No") : Summary.default("Yes")} help="Disable a step to prevent it from running.">
                                <Checkbox value={!action.IsDisabled} onChange={(IsDisabled) => this.setActionMetaProperties({ IsDisabled: !IsDisabled })} label="Enabled" />
                            </ExpandableFormSection>

                            <ExecutionPlan
                                projectId={projectContext.state.model.Id}
                                expandedByDefault={this.props.isNew}
                                doBusyTask={this.doBusyForChildren}
                                executionLocation={this.props.stepOther.plugin.executionLocation}
                                runOnServerOrWorkerPoolCopy={this.state.runOnServerOrWorkerPoolCopy}
                                runOn={this.state.runOn}
                                onRunOnChanged={this.onRunOnChanged}
                                targetRoleOption={this.props.stepOther.plugin.targetRoleOption(action)}
                                targetRoles={step.Properties["Octopus.Action.TargetRoles"] as string}
                                disableAddTargetRoles={this.props.stepOther.plugin.disableAddTargetRoles}
                                onTargetRolesChanged={(roles) => this.setStepProperties({ ["Octopus.Action.TargetRoles"]: ParseHelper.encodeCSV(roles) })}
                                targetRolesError={this.getFieldError("Octopus.Action.TargetRoles")}
                                isChildStep={contextSelectors.isChildAction(action.Id)}
                                maxParallelism={step.Properties["Octopus.Action.MaxParallelism"] as string}
                                onMaxParallelismChanged={(max) => this.setStepProperties({ ["Octopus.Action.MaxParallelism"]: max })}
                                availableRoles={stepLookups.machineRoles}
                                availableWorkerPools={stepLookups.availableWorkerPools}
                                canRunOnWorker={contextSelectors.canActionRunOnWorker(action.Id)}
                                isBuiltInWorkerEnabled={stepOther.isBuiltInWorkerEnabled}
                                targetWorkerPool={action.WorkerPoolId}
                                targetWorkerPoolVariable={action.WorkerPoolVariable}
                                onTargetWorkerPoolChanged={(workerPoolNameOrId, workerPoolVariable) => this.setActionMetaProperties({ WorkerPoolId: workerPoolNameOrId, WorkerPoolVariable: workerPoolVariable })}
                                runsOnServer={runsOnServer(action, this.props.stepOther.plugin.executionLocation)}
                                getFieldError={this.getFieldError}
                                feeds={this.props.feeds}
                                loadFeeds={this.loadFeeds}
                                canRunInContainer={this.canRunInContainer()}
                                imageNameError={this.getFieldError("Container.FeedId")}
                            />

                            {!stepLookups.actionTemplate && (
                                <div>
                                    <ProjectActionPropertiesEditor
                                        plugin={this.props.stepOther.plugin}
                                        projectId={projectContext.state.model.Id}
                                        isNew={this.isNew}
                                        doBusyTask={this.doBusyForChildren}
                                        busy={busy}
                                        inputs={action.Inputs}
                                        properties={action.Properties}
                                        packages={action.Packages}
                                        runOn={this.state.runOn}
                                        setInputs={(inputs, callback) => this.setActionInputs(inputs, callback)}
                                        setProperties={(p, i, c) => this.setActionProperties(p, c)}
                                        setPackages={(p) => this.setActionPackages(p)}
                                        additionalActions={actionEditorAdditionalActions}
                                        getFieldError={this.getFieldError}
                                        errors={errors}
                                        expandedByDefault={this.props.isNew}
                                        refreshRunOn={() => this.refreshRunOn()}
                                        getProcessResource={contextSelectors.getProcessResource}
                                    />

                                    {processContext.selectors.actionHasFeatures(action.Id) && (
                                        <FeatureEditor
                                            plugin={this.props.stepOther.plugin}
                                            projectId={projectContext.state.model.Id}
                                            isNew={this.isNew}
                                            doBusyTask={this.doBusyForChildren}
                                            busy={busy}
                                            properties={action.Properties}
                                            packages={action.Packages}
                                            runOn={this.state.runOn}
                                            setProperties={(p, i, c) => this.setActionProperties(p, c)}
                                            setPackages={(p) => this.setActionPackages(p)}
                                            enabledFeatures={(action.Properties["Octopus.Action.EnabledFeatures"] as string) || ""}
                                            getFieldError={this.getFieldError}
                                            errors={errors}
                                            expandedByDefault={this.props.isNew}
                                            openFeaturesElement={addFeaturesElement}
                                            refreshRunOn={() => this.refreshRunOn()}
                                        />
                                    )}
                                </div>
                            )}

                            {stepLookups.actionTemplate && (
                                <ActionTemplateEditor
                                    actionTemplate={stepLookups.actionTemplate}
                                    projectId={projectContext.state.model.Id}
                                    process={processContext.state.model}
                                    processType={processContext.selectors.getProcessType()}
                                    actionId={action.Id}
                                    properties={action.Properties}
                                    setProperties={(p) => this.setActionProperties(p)}
                                    setPackages={(p) => this.setActionPackages(p)}
                                    doBusyTask={this.doBusyForChildren}
                                    onActionTemplateUpdate={processContext.actions.refreshFromServer}
                                />
                            )}

                            <FormSectionHeading title="Conditions" />
                            <Environments
                                environmentOption={this.state.environmentOption}
                                hasHiddenEnvironments={this.hasHiddenEnvironments}
                                environments={this.props.stepLookups.environments}
                                inclusiveEnvironments={action.Environments}
                                exclusiveEnvironments={action.ExcludedEnvironments}
                                onEnvironmentOptionChanged={(environmentOption) => this.updateEnvironmentOption(environmentOption)}
                                onInclusiveEnvironmentsChanged={(val) => this.updateEnvironmentSelection({ ...this.environmentSelection, inclusive: val })}
                                onExclusiveEnvironmentsChanged={(val) => this.updateEnvironmentSelection({ ...this.environmentSelection, exclusive: val })}
                                onInclusiveEnvironmentRemoved={this.handleInclusiveEnvironmentRemoval}
                                onExclusiveEnvironmentRemoved={this.handleExclusiveEnvironmentRemoval}
                            />

                            {processContext.state.processType === ProcessType.Deployment && (action.Channels.length > 0 || stepLookups.channels.length > 1) && (
                                <ExpandableFormSection title="Channels" help="Choose which channels this step applies to." summary={this.channelsSummary()} errorKey="channels">
                                    <Note>If nothing is selected this step will run for releases in any channel, otherwise it will only run for releases belonging to the selected channels.</Note>
                                    <ChannelMultiSelect items={stepLookups.channels} onChange={(val) => this.setActionMetaProperties({ Channels: val })} value={action.Channels} />
                                </ExpandableFormSection>
                            )}

                            <FeatureToggle feature={Feature.MultiTenancy}>
                                {(projectContext.state.model.TenantedDeploymentMode !== TenantedDeploymentMode.Untenanted || action.TenantTags.length > 0) && (
                                    <TenantsExpander doBusyTask={this.doBusyForChildren} tenantTags={action.TenantTags} tagIndex={stepLookups.tagIndex} onTenantTagsChange={(tags) => this.setActionMetaProperties({ TenantTags: tags })} />
                                )}
                            </FeatureToggle>

                            {!this.isChildAction() && step && (
                                <RunTriggerExpander
                                    isFirstStep={contextSelectors.isFirstStep(step.Id)}
                                    condition={step.Condition}
                                    onConditionChange={(val) => this.setStepMetaProperties({ Condition: val })}
                                    variableExpression={step.Properties["Octopus.Step.ConditionVariableExpression"] as string}
                                    onVariableExpressionChange={(x) => this.setStepProperties({ ["Octopus.Step.ConditionVariableExpression"]: x })}
                                    projectId={projectContext.state.model.Id}
                                    variableExpressionError={this.getFieldError("ConditionVariableExpression")}
                                />
                            )}
                            {this.isChildAction() && action && (
                                <RunTriggerForChildActionExpander
                                    isFirstStep={contextSelectors.isFirstChildAction(action.Id)}
                                    condition={action.Condition || RunConditionForAction.Success}
                                    onConditionChange={(val) => this.setActionMetaProperties({ Condition: val })}
                                    variableExpression={action.Properties["Octopus.Action.ConditionVariableExpression"] as string}
                                    onVariableExpressionChange={(x) => this.setActionProperties({ ["Octopus.Action.ConditionVariableExpression"]: x })}
                                    projectId={projectContext.state.model.Id}
                                    variableExpressionError={this.getFieldError("ConditionVariableExpression")}
                                />
                            )}

                            {contextSelectors.shouldShowRunTrigger(action.Id) && <StartTriggerExpander startTrigger={step.StartTrigger} onChange={(val) => this.setStepMetaProperties({ StartTrigger: val })} />}

                            {contextSelectors.shouldShowPackageRequirementOptionForAction(action.Id) && step && (
                                <PackageRequirementExpander
                                    packageRequirement={step.PackageRequirement}
                                    onChange={(val) => {
                                        this.setStepMetaProperties({ PackageRequirement: val });
                                        if (val === PackageRequirement.AfterPackageAcquisition) this.props.processContext.actions.resetPackageRequirementAfterPackageAcquisitionStep();
                                    }}
                                />
                            )}
                            <ExpandableFormSection
                                title="Required"
                                summary={
                                    action.IsRequired || hasManualInterventionResponsibleTeams
                                        ? Summary.summary(
                                              <span>
                                                  This step is <strong>required</strong> and cannot be skipped
                                              </span>
                                          )
                                        : Summary.summary(
                                              <span>
                                                  This step is <strong>not required</strong> and can be skipped
                                              </span>
                                          )
                                }
                                help="Required steps cannot be skipped when deploying a release"
                                errorKey="required"
                            >
                                {hasManualInterventionResponsibleTeams && <Note>Responsible teams are specified, therefore this step is always required.</Note>}

                                <Checkbox
                                    value={action.IsRequired || hasManualInterventionResponsibleTeams}
                                    label="Prevent this step from being skipped when deploying"
                                    disabled={hasManualInterventionResponsibleTeams}
                                    onChange={(val) => this.setActionMetaProperties({ IsRequired: val })}
                                />
                            </ExpandableFormSection>
                        </div>
                    </ScriptActionContext.Provider>
                </PaperLayout>
            </ExpandableContainer>
        );
    }

    handleExclusiveEnvironmentRemoval = (value: string) => {
        //This only applies to version-controlled projects because when the project is version controlled we need to be able
        //to remove environments which have been marked as unavailable. This is something that can easily happen by simply
        //renaming an environment for example. It's not a missing id for example, it's just a name so we should be able
        //to remove it and add something that makes sense.
        const isVersionControlled = isVersionControlledProcess(this.props.projectContext.state.model.IsVersionControlled, this.props.processContext.selectors.getProcessType(), !!this.props.isConfigurationAsCodeForRunbooksEnabled);
        if (!isVersionControlled) {
            return;
        }

        const previous = this.environmentSelection;
        const unavailableExclusive = previous.unavailableExclusive.filter((x) => x !== value);
        const exclusive = previous.exclusive.filter((x) => x !== value);

        this.updateEnvironmentSelection({
            ...previous,
            exclusive,
            unavailableExclusive,
        });
    };

    handleInclusiveEnvironmentRemoval = (value: string) => {
        //This only applies to version-controlled projects because when the project is version controlled we need to be able
        //to remove environments which have been marked as unavailable. This is something that can easily happen by simply
        //renaming an environment for example. It's not a missing id for example, it's just a name so we should be able
        //to remove it and add something that makes sense.
        const isVersionControlled = isVersionControlledProcess(this.props.projectContext.state.model.IsVersionControlled, this.props.processContext.selectors.getProcessType(), !!this.props.isConfigurationAsCodeForRunbooksEnabled);
        if (!isVersionControlled) {
            return;
        }

        const previous = this.environmentSelection;
        const unavailable = previous.unavailable.filter((x) => x !== value);
        const inclusive = previous.inclusive.filter((x) => x !== value);

        this.updateEnvironmentSelection({
            ...previous,
            inclusive,
            unavailable,
        });
    };

    private updateEnvironmentOption(environmentOption: EnvironmentOption) {
        this.setState({ environmentOption }, () => {
            this.updateEnvironments(this.environmentSelection, environmentOption);
        });
    }

    private updateEnvironmentSelection(environmentSelection: EnvironmentSelection) {
        this.updateEnvironments(environmentSelection, this.state.environmentOption);
    }

    private updateEnvironments(selection: EnvironmentSelection | null, environmentOption: EnvironmentOption | null) {
        const environments = selection ? uniq((selection.inclusive || []).concat(selection.unavailable)) : [];
        const excludedEnvironments = selection ? uniq((selection.exclusive || []).concat(selection.unavailableExclusive)) : [];

        if (environmentOption && environmentOption !== EnvironmentOption.Include) {
            environments.splice(0);
        }
        if (environmentOption && environmentOption !== EnvironmentOption.Exclude) {
            excludedEnvironments.splice(0);
        }

        this.setActionMetaProperties({ Environments: environments, ExcludedEnvironments: excludedEnvironments });
    }

    private getFieldError = (value: string): string => {
        const error = this.props.processErrorSelectors.getActionFieldError(this.props.action.Id, this.props.processContext.selectors, value);
        return error;
    };

    private getFieldWarning = (value: string): string => {
        const warning = this.props.processWarningSelectors.getActionFieldWarning(this.props.action.Id, this.props.processContext.selectors, value);
        return warning;
    };

    private onRunOnChanged = (runOn: RunOn) => {
        if (this.state.runOn && isRunOnServerOrWorkerPool(this.state.runOn)) {
            this.setState({ runOnServerOrWorkerPoolCopy: this.state.runOn });
        }

        this.setState({ runOn }, () => {
            this.props.processContext.actions.runOnChanged(this.props.action.Id, this.props.step.Id, runOn);
        });
    };

    private loadFeeds = async (callback?: (feeds: FeedResource[]) => void) => {
        await this.props.doBusyTask(async () => {
            await this.props.refreshFeeds();
            if (callback) {
                callback(this.props.feeds);
            }
        });
    };

    private handleEnabledToggle = async () => {
        const action = this.props.action;
        this.setActionMetaProperties({ IsDisabled: !action.IsDisabled });
    };

    private refreshStepLookupsImmediately() {
        //TODO: This is not ideal. We need this to happen after a setState is called, and we don't have a side effect
        // in our reducers atm, so we can't just say `setState(..., () => nowDoThis)`, hence the setImmediate.
        setImmediate(() => this.props.refreshStepLookups());
    }

    private handleDetachStepTemplate = async () => {
        const action = this.props.action;
        this.props.processContext.actions.removeActionProperties(action.Id, ["Octopus.Action.Template.Id", "Octopus.Action.Template.Version"]);
        this.refreshStepLookupsImmediately();
    };

    private handleCreateStepTemplate = async () => {
        const templateExists = (templates: ActionTemplateResource[], actionName: string) => {
            return templates.some((s) => s.Name.toLocaleUpperCase() === actionName.toLocaleUpperCase());
        };

        const getNewTemplateName = (templates: ActionTemplateResource[], action: DeploymentActionResource) => {
            let suffix = "";
            let counter = 1;
            while (templateExists(templates, action.Name + suffix)) {
                suffix = " (" + counter + ")";
                counter++;
            }
            return action.Name + suffix;
        };

        const createStepTemplateFromAction = async (action: DeploymentActionResource) => {
            const existingTemplates = await repository.ActionTemplates.all();
            const newName = getNewTemplateName(existingTemplates, action);
            const newTemplate = JSON.parse(JSON.stringify(action));
            newTemplate.Name = newName;
            newTemplate.Id = generateGuid();
            newTemplate.Version = 1;
            newTemplate.Description = "Created from step '" + this.props.action.Name + "' in project '" + this.props.projectContext.state.model.Name + "'";
            return newTemplate;
        };

        await this.props.doBusyTask(async () => {
            const newTemplate = await createStepTemplateFromAction(this.props.action);
            const result = await repository.ActionTemplates.create(newTemplate);
            if (result) {
                this.setActionProperties({ ["Octopus.Action.Template.Id"]: result.Id, "Octopus.Action.Template.Version": result.Version.toString() });
                this.refreshStepLookupsImmediately();
            }
        });
    };

    private canRunInContainer() {
        return this.props.stepOther.plugin.canRunInContainer === false ? this.props.stepOther.plugin.canRunInContainer : true;
    }

    private notesSummary() {
        const action = this.props.action;
        return action && action.Notes ? Summary.summary(<Markdown markup={action.Notes} />) : Summary.placeholder("None");
    }

    private channelsSummary() {
        const action = this.props.action;
        return action && action.Channels.length > 0 ? Summary.summary(<span>This step will only run for releases in {action.Channels.map((ch) => this.getChipForChannel(ch))}</span>) : Summary.default("This step will run for releases in any channel");
    }

    private getChipForChannel(nameOrId: string) {
        return (
            <Lookup
                lookupCollection={this.props.stepLookups.channels}
                lookupId={nameOrId}
                getIdFromElement={(element) => element[this.props.itemKey]}
                render={(channel) => <ChannelChip key={nameOrId} channelName={channel.Name} />}
                renderFallback={<ContextualMissingChip key={nameOrId} lookupKey={nameOrId} type={ChipIcon.Channel} />}
            />
        );
    }

    private setActionInputs = (inputs: ActionInputs, callback?: () => void) => {
        const action = this.props.action;
        this.props.processContext.actions.setActionInputs(action.Id, inputs);
        if (callback) {
            callback();
        }
    };

    private setActionProperties = (properties: Partial<ActionProperties>, callback?: () => void) => {
        const action = this.props.action;
        this.props.processContext.actions.setActionProperties(action.Id, properties);
        if (callback) {
            callback();
        }
    };

    private setActionPackages = (packages: PackageReference[]) => {
        this.setActionMetaProperties({ Packages: packages });
        const processContext = this.props.processContext;
        if (processContext.selectors.getStepRightAfterPackageAcquisition()?.Id === this.props.step.Id) processContext.actions.resetPackageRequirementAfterPackageAcquisitionStep();
    };

    private setStepProperties = (properties: Partial<ActionProperties>) => {
        const step = this.props.step;
        this.props.processContext.actions.setStepProperties(step.Id, properties);
    };

    private setActionMetaProperties<K extends keyof StoredAction>(state: Pick<StoredAction, K> | StoredAction, callback?: () => void) {
        const action = this.props.action;
        this.props.processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, ...state }));
        if (callback) {
            callback();
        }
    }

    private setStepMetaProperties<K extends keyof StoredStep>(state: Pick<StoredStep, K>, callback?: () => void) {
        const step = this.props.step;
        this.props.processContext.actions.setStepMetaProperties(step.Id, (prev) => ({ ...prev, ...state }));
        if (callback) {
            callback();
        }
    }
}

const isConfigurationAsCodeForRunbooksEnabledSelector = (state: GlobalState) => state.configurationArea.features.isConfigurationAsCodeForRunbooksEnabled;
const isBuiltInWorkerEnabledSelector = (state: GlobalState) => state.configurationArea.features.isBuiltInWorkerEnabled;

const ProcessActionDetails: React.FC<ProcessActionDetailsProps> = (props) => {
    const isConfigurationAsCodeForRunbooksEnabled = useSelector(isConfigurationAsCodeForRunbooksEnabledSelector);
    const isBuiltInWorkerEnabled = useSelector(isBuiltInWorkerEnabledSelector);
    const projectContext = useProjectContext();
    const processContext = useProcessContext();
    const processErrorSelectors = useProcessErrorSelectors();
    const processWarningSelectors = useProcessWarningSelectors();
    const processQueryStringContext = useProcessQueryStringContext();
    const itemKey = useKeyedItemAccess();
    const runbookContext = useOptionalRunbookContext();
    const feeds = useFeedsFromContext();
    const actionTemplates = useActionTemplatesFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();

    return (
        <ProcessActionDetailsInternal
            {...props}
            feeds={feeds}
            refreshFeeds={refreshFeeds}
            actionTemplates={actionTemplates}
            projectContext={projectContext}
            processContext={processContext}
            processErrorSelectors={processErrorSelectors}
            processWarningSelectors={processWarningSelectors}
            processQueryStringContext={processQueryStringContext}
            runbookContext={runbookContext}
            isConfigurationAsCodeForRunbooksEnabled={isConfigurationAsCodeForRunbooksEnabled}
            isBuiltInWorkerEnabled={isBuiltInWorkerEnabled}
            itemKey={itemKey}
        />
    );
};

export default ProcessActionDetails;
